import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Plus, Trash2, ArrowLeft, MoreHorizontal, GripVertical,
  Image as ImageIcon, Film, X, Loader2, Edit3, Check,
  LayoutGrid, Paperclip, Search, ChevronRight, AlertTriangle,
  Palette, ChevronsLeftRight, Minus, Settings2, Calendar, Type, Hash, CheckSquare, Tag,
  Lock, Users,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import {
  KanbanCard, BoardData, KanbanBoardWithCounts,
  fetchBoardsWithCounts, createBoard, updateBoard, deleteBoard,
  fetchBoardData, createColumn, updateColumn, deleteColumn, reorderColumns,
  createCard, updateCard, deleteCard, moveCard, reorderCards,
  fetchOrgDirectory,
  CustomFieldDefinition, CustomFieldType, BoardVisibility, OrgPerson,
  newCustomFieldId, isCustomFieldType, KANBAN_LIMITS,
} from '../lib/kanbanService';
import { MediaAsset, backfillMissingThumbnails, fetchOrgMediaAssets, galleryThumbSrc, isImageAsset, assetDisplayName } from '../lib/mediaGallery';
import KanbanSelect from '../components/kanban/KanbanSelect';
import '../kanban.css';

type View = 'list' | 'board';

const COLUMN_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f59e0b', '#10b981', '#06b6d4', '#64748b',
];

export default function KanbanBoardPage() {
  const { user } = useAuth();
  const { org, role } = useOrg();
  const orgId = org?.id || null;

  const [view, setView] = useState<View>('list');
  const [boards, setBoards] = useState<KanbanBoardWithCounts[]>([]);
  const [boardData, setBoardData] = useState<BoardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // New board form
  const [showNewBoard, setShowNewBoard] = useState(false);
  const [newBoardTitle, setNewBoardTitle] = useState('');
  const [newBoardDesc, setNewBoardDesc] = useState('');
  const [newBoardVisibility, setNewBoardVisibility] = useState<BoardVisibility>('org');
  const [boardListFilter, setBoardListFilter] = useState<'all' | BoardVisibility>('all');
  const [people, setPeople] = useState<OrgPerson[]>([]);

  // Card modal
  const [cardModal, setCardModal] = useState<{
    mode: 'create' | 'edit';
    columnId: string;
    card?: KanbanCard;
  } | null>(null);
  const [cardTitle, setCardTitle] = useState('');
  const [cardDesc, setCardDesc] = useState('');
  const [cardMediaIds, setCardMediaIds] = useState<string[]>([]);
  const [cardCustomValues, setCardCustomValues] = useState<Record<string, any>>({});
  const [cardAssignees, setCardAssignees] = useState<string[]>([]);
  const [cardFieldName, setCardFieldName] = useState('');
  const [cardFieldType, setCardFieldType] = useState<CustomFieldType>('text');
  const [cardFieldOptions, setCardFieldOptions] = useState('');

  // Custom Fields manager modal
  const [showFieldsModal, setShowFieldsModal] = useState(false);
  const [newFieldName, setNewFieldName] = useState('');
  const [newFieldType, setNewFieldType] = useState<CustomFieldType>('text');
  const [newFieldOptions, setNewFieldOptions] = useState('');

  // Media picker
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [galleryAssets, setGalleryAssets] = useState<MediaAsset[]>([]);
  const [galleryLoading, setGalleryLoading] = useState(false);

  // New column
  const [showNewColumn, setShowNewColumn] = useState(false);
  const [newColumnTitle, setNewColumnTitle] = useState('');

  // Column editing
  const [editingColumnId, setEditingColumnId] = useState<string | null>(null);
  const [editingColumnTitle, setEditingColumnTitle] = useState('');

  // Board title inline edit
  const [editingBoardTitle, setEditingBoardTitle] = useState(false);
  const [boardTitleDraft, setBoardTitleDraft] = useState('');

  // Collapsed columns
  const [collapsedCols, setCollapsedCols] = useState<Set<string>>(new Set());

  // WIP limits (local state, keyed by column id)
  const [wipLimits, setWipLimits] = useState<Record<string, number>>({});

  // Filter
  const [filterText, setFilterText] = useState('');

  // Quick-add per column
  const [quickAddColId, setQuickAddColId] = useState<string | null>(null);
  const [quickAddTitle, setQuickAddTitle] = useState('');
  const quickAddRef = useRef<HTMLInputElement>(null);

  // Card drag state
  const [dragCardId, setDragCardId] = useState<string | null>(null);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);
  const [dragOverCardIdx, setDragOverCardIdx] = useState<number | null>(null);

  // Column drag state
  const [dragColId, setDragColId] = useState<string | null>(null);
  const [dragOverColIdx, setDragOverColIdx] = useState<number | null>(null);

  // Media assets cache for card thumbnails
  const [mediaCache, setMediaCache] = useState<Record<string, MediaAsset>>({});

  // Column context menu
  const [columnMenuId, setColumnMenuId] = useState<string | null>(null);
  const [showColorPicker, setShowColorPicker] = useState<string | null>(null);
  const [showWipInput, setShowWipInput] = useState<string | null>(null);
  const [wipDraft, setWipDraft] = useState('');
  const colMenuRef = useRef<HTMLDivElement>(null);

  // Close menus on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (colMenuRef.current && !colMenuRef.current.contains(e.target as Node)) {
        setColumnMenuId(null);
        setShowColorPicker(null);
        setShowWipInput(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  /* ─── Fetch boards list ─── */
  const loadBoards = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      const data = await fetchBoardsWithCounts(orgId);
      setBoards(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { loadBoards(); }, [loadBoards]);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    fetchOrgDirectory(orgId)
      .then(rows => { if (!cancelled) setPeople(rows); })
      .catch(() => { if (!cancelled) setPeople([]); });
    return () => { cancelled = true; };
  }, [orgId]);

  const personName = (id: string) => {
    const found = people.find(p => p.user_id === id);
    return found?.full_name?.trim() || 'Membro';
  };

  /* ─── Open a board ─── */
  const openBoard = useCallback(async (boardId: string) => {
    try {
      setLoading(true);
      setFilterText('');
      setCollapsedCols(new Set());
      const data = await fetchBoardData(boardId);
      if (data) {
        setBoardData(data);
        setView('board');

        // Pre-cache media assets used by cards
        const allMediaIds = new Set<string>();
        data.columns.forEach(col =>
          col.cards.forEach(card =>
            (card.media_asset_ids || []).forEach(id => allMediaIds.add(id))
          )
        );
        if (allMediaIds.size > 0 && orgId) {
          const assets = await fetchOrgMediaAssets(orgId);
          const cache: Record<string, MediaAsset> = {};
          assets.forEach(a => { cache[a.id] = a; });
          setMediaCache(cache);
          void backfillMissingThumbnails(orgId, assets, (id, thumbnailUrl) => {
            setMediaCache((prev) => prev[id] ? { ...prev, [id]: { ...prev[id], thumbnail_url: thumbnailUrl } } : prev);
          });
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  /* ─── Create board ─── */
  const handleCreateBoard = async () => {
    if (!newBoardTitle.trim() || !orgId || !user?.id) return;
    try {
      const board = await createBoard(orgId, newBoardTitle.trim(), user.id, newBoardDesc.trim() || undefined, newBoardVisibility);
      setNewBoardTitle('');
      setNewBoardDesc('');
      setNewBoardVisibility('org');
      setShowNewBoard(false);
      openBoard(board.id);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleSetVisibility = async (visibility: BoardVisibility) => {
    if (!boardData) return;
    try {
      await updateBoard(boardData.board.id, { visibility });
      setBoardData(prev => prev ? { ...prev, board: { ...prev.board, visibility } } : null);
      setBoards(prev => prev.map(b => b.id === boardData.board.id ? { ...b, visibility } : b));
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Delete board ─── */
  const handleDeleteBoard = async (boardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!window.confirm('Excluir este quadro e todos os seus cards?')) return;
    try {
      await deleteBoard(boardId);
      setBoards(prev => prev.filter(b => b.id !== boardId));
      if (boardData?.board.id === boardId) {
        setBoardData(null);
        setView('list');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Update board title inline ─── */
  const handleSaveBoardTitle = async () => {
    if (!boardTitleDraft.trim() || !boardData) return;
    try {
      await updateBoard(boardData.board.id, { title: boardTitleDraft.trim() });
      setBoardData(prev => prev ? { ...prev, board: { ...prev.board, title: boardTitleDraft.trim() } } : null);
      setEditingBoardTitle(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Create column ─── */
  const handleCreateColumn = async () => {
    if (!newColumnTitle.trim() || !boardData) return;
    try {
      const pos = boardData.columns.length;
      const col = await createColumn(boardData.board.id, newColumnTitle.trim(), pos);
      setBoardData(prev => prev ? {
        ...prev,
        columns: [...prev.columns, { ...col, cards: [] }],
      } : null);
      setNewColumnTitle('');
      setShowNewColumn(false);
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Update column title ─── */
  const handleUpdateColumnTitle = async (colId: string) => {
    if (!editingColumnTitle.trim()) return;
    try {
      await updateColumn(colId, { title: editingColumnTitle.trim() });
      setBoardData(prev => prev ? {
        ...prev,
        columns: prev.columns.map(c => c.id === colId ? { ...c, title: editingColumnTitle.trim() } : c),
      } : null);
      setEditingColumnId(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Update column color ─── */
  const handleUpdateColumnColor = async (colId: string, color: string) => {
    try {
      await updateColumn(colId, { color });
      setBoardData(prev => prev ? {
        ...prev,
        columns: prev.columns.map(c => c.id === colId ? { ...c, color } : c),
      } : null);
      setShowColorPicker(null);
      setColumnMenuId(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Delete column ─── */
  const handleDeleteColumn = async (colId: string) => {
    if (!window.confirm('Excluir esta coluna e todos os seus cards?')) return;
    try {
      await deleteColumn(colId);
      setBoardData(prev => prev ? {
        ...prev,
        columns: prev.columns.filter(c => c.id !== colId),
      } : null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Toggle collapse ─── */
  const toggleCollapse = (colId: string) => {
    setCollapsedCols(prev => {
      const next = new Set(prev);
      next.has(colId) ? next.delete(colId) : next.add(colId);
      return next;
    });
  };

  /* ─── WIP Limit ─── */
  const handleSetWipLimit = (colId: string) => {
    const val = parseInt(wipDraft, 10);
    if (!isNaN(val) && val >= 0) {
      setWipLimits(prev => ({ ...prev, [colId]: val === 0 ? 0 : val }));
    } else {
      // Remove limit
      setWipLimits(prev => {
        const next = { ...prev };
        delete next[colId];
        return next;
      });
    }
    setShowWipInput(null);
    setColumnMenuId(null);
  };

  /* ─── Open card modal ─── */
  const openCardModal = (mode: 'create' | 'edit', columnId: string, card?: KanbanCard) => {
    setCardModal({ mode, columnId, card });
    setCardTitle(card?.title || '');
    setCardDesc(card?.description || '');
    setCardMediaIds(card?.media_asset_ids || []);
    setCardCustomValues(card?.custom_field_values || {});
    setCardAssignees(card?.assignee_ids || []);
    setCardFieldName('');
    setCardFieldType('text');
    setCardFieldOptions('');
    setShowMediaPicker(false);
  };

  /* ─── Save card ─── */
  const allowedPeople = () => new Set(people.map(p => p.user_id));
  const allowedMedia = () => new Set([
    ...galleryAssets.map(a => a.id),
    ...Object.keys(mediaCache),
  ]);

  const handleSaveCard = async () => {
    if (!cardTitle.trim() || !cardModal || !user?.id) return;
    try {
      if (cardModal.mode === 'create') {
        const col = boardData?.columns.find(c => c.id === cardModal.columnId);
        const pos = col ? col.cards.length : 0;
        const card = await createCard(cardModal.columnId, cardTitle.trim(), user.id, {
          description: cardDesc.trim() || undefined,
          position: pos,
          media_asset_ids: cardMediaIds,
          custom_field_values: cardCustomValues,
          assignee_ids: cardAssignees,
          fields: boardData?.board.custom_fields,
          allowedAssignees: allowedPeople(),
          allowedMedia: allowedMedia(),
        });
        setBoardData(prev => prev ? {
          ...prev,
          columns: prev.columns.map(c =>
            c.id === cardModal.columnId ? { ...c, cards: [...c.cards, card] } : c
          ),
        } : null);
      } else if (cardModal.card) {
        await updateCard(cardModal.card.id, {
          title: cardTitle.trim(),
          description: cardDesc.trim() || undefined,
          media_asset_ids: cardMediaIds,
          custom_field_values: cardCustomValues,
          assignee_ids: cardAssignees,
          fields: boardData?.board.custom_fields,
          allowedAssignees: allowedPeople(),
          allowedMedia: allowedMedia(),
        });
        setBoardData(prev => prev ? {
          ...prev,
          columns: prev.columns.map(c => ({
            ...c,
            cards: c.cards.map(card =>
              card.id === cardModal.card!.id
                ? { ...card, title: cardTitle.trim(), description: cardDesc.trim(), media_asset_ids: cardMediaIds, custom_field_values: cardCustomValues, assignee_ids: cardAssignees }
                : card
            ),
          })),
        } : null);
      }
      setCardModal(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Custom Fields Management ─── */
  const handleAddCustomField = async () => {
    if (!newFieldName.trim() || !boardData) return;
    if (!isCustomFieldType(newFieldType)) return;
    const options = newFieldType === 'select' 
      ? newFieldOptions.split(',').map(o => o.trim()).filter(Boolean)
      : undefined;
    const newField: CustomFieldDefinition = {
      id: newCustomFieldId(),
      name: newFieldName.trim(),
      type: newFieldType,
      options: options && options.length > 0 ? options : undefined,
    };
    const currentFields = boardData.board.custom_fields || [];
    const updatedFields = [...currentFields, newField];
    try {
      await updateBoard(boardData.board.id, { custom_fields: updatedFields });
      setBoardData(prev => prev ? { ...prev, board: { ...prev.board, custom_fields: updatedFields } } : null);
      setNewFieldName('');
      setNewFieldOptions('');
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddFieldFromCard = async () => {
    if (!cardFieldName.trim() || !boardData || !isCustomFieldType(cardFieldType)) return;
    const options = cardFieldType === 'select'
      ? cardFieldOptions.split(',').map(o => o.trim()).filter(Boolean)
      : undefined;
    if (cardFieldType === 'select' && (!options || options.length === 0)) {
      setError('Informe ao menos uma opção, separada por vírgula.');
      return;
    }
    const newField: CustomFieldDefinition = {
      id: newCustomFieldId(),
      name: cardFieldName.trim(),
      type: cardFieldType,
      options: options && options.length > 0 ? options : undefined,
    };
    const updatedFields = [...(boardData.board.custom_fields || []), newField];
    try {
      await updateBoard(boardData.board.id, { custom_fields: updatedFields });
      setBoardData(prev => prev ? { ...prev, board: { ...prev.board, custom_fields: updatedFields } } : null);
      setCardCustomValues(prev => ({ ...prev, [newField.id]: newField.type === 'checkbox' ? false : '' }));
      setCardFieldName('');
      setCardFieldOptions('');
      setCardFieldType('text');
      setError(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveCustomField = async (fieldId: string) => {
    if (!boardData || !window.confirm('Remover este campo personalizado do quadro?')) return;
    const currentFields = boardData.board.custom_fields || [];
    const updatedFields = currentFields.filter(f => f.id !== fieldId);
    try {
      await updateBoard(boardData.board.id, { custom_fields: updatedFields });
      setBoardData(prev => prev ? { ...prev, board: { ...prev.board, custom_fields: updatedFields } } : null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  const getFieldIcon = (type: CustomFieldType) => {
    switch (type) {
      case 'date': return <Calendar size={12} />;
      case 'number': return <Hash size={12} />;
      case 'checkbox': return <CheckSquare size={12} />;
      case 'select': return <Tag size={12} />;
      default: return <Type size={12} />;
    }
  };

  /* ─── Quick-add card ─── */
  const handleQuickAdd = async (columnId: string) => {
    if (!quickAddTitle.trim() || !user?.id) return;
    try {
      const col = boardData?.columns.find(c => c.id === columnId);
      const pos = col ? col.cards.length : 0;
      const card = await createCard(columnId, quickAddTitle.trim(), user.id, { position: pos });
      setBoardData(prev => prev ? {
        ...prev,
        columns: prev.columns.map(c =>
          c.id === columnId ? { ...c, cards: [...c.cards, card] } : c
        ),
      } : null);
      setQuickAddTitle('');
      // Keep focus on the input for rapid entry
      quickAddRef.current?.focus();
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ─── Delete card ─── */
  const handleDeleteCard = async (cardId: string, columnId: string) => {
    if (!window.confirm('Excluir este card?')) return;
    try {
      await deleteCard(cardId);
      setBoardData(prev => prev ? {
        ...prev,
        columns: prev.columns.map(c =>
          c.id === columnId ? { ...c, cards: c.cards.filter(card => card.id !== cardId) } : c
        ),
      } : null);
      if (cardModal?.card?.id === cardId) setCardModal(null);
    } catch (err: any) {
      setError(err.message);
    }
  };

  /* ═══════════════════════════════════════════
     CARD DRAG & DROP (between + within columns)
     ═══════════════════════════════════════════ */
  const handleCardDragStart = (e: React.DragEvent, cardId: string) => {
    setDragCardId(cardId);
    setDragColId(null); // Ensure column drag is off
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/kanban-card', cardId);
    requestAnimationFrame(() => {
      const el = document.getElementById(`kanban-card-${cardId}`);
      if (el) el.classList.add('kanban-card-dragging');
    });
  };

  const handleCardDragEnd = () => {
    if (dragCardId) {
      const el = document.getElementById(`kanban-card-${dragCardId}`);
      if (el) el.classList.remove('kanban-card-dragging');
    }
    setDragCardId(null);
    setDragOverColumnId(null);
    setDragOverCardIdx(null);
  };

  const handleCardDragOverColumn = (e: React.DragEvent, columnId: string) => {
    if (!dragCardId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnId(columnId);
  };

  const handleCardDragOverCard = (e: React.DragEvent, columnId: string, cardIndex: number) => {
    if (!dragCardId) return;
    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = 'move';
    setDragOverColumnId(columnId);

    // Calculate if cursor is above or below middle of the card
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const insertIdx = e.clientY < midY ? cardIndex : cardIndex + 1;
    setDragOverCardIdx(insertIdx);
  };

  const handleCardDragLeaveColumn = (e: React.DragEvent, columnId: string) => {
    if (!dragCardId) return;
    const relatedTarget = e.relatedTarget as HTMLElement | null;
    const column = document.getElementById(`kanban-col-${columnId}`);
    if (column && relatedTarget && column.contains(relatedTarget)) return;
    if (dragOverColumnId === columnId) {
      setDragOverColumnId(null);
      setDragOverCardIdx(null);
    }
  };

  const handleCardDrop = async (e: React.DragEvent, targetColumnId: string) => {
    e.preventDefault();
    const cardId = e.dataTransfer.getData('application/kanban-card');
    if (!cardId || !boardData) {
      setDragOverColumnId(null);
      setDragOverCardIdx(null);
      return;
    }

    // Find source
    let sourceCard: KanbanCard | undefined;
    let sourceColumnId: string | undefined;
    for (const col of boardData.columns) {
      const found = col.cards.find(c => c.id === cardId);
      if (found) { sourceCard = found; sourceColumnId = col.id; break; }
    }
    if (!sourceCard || !sourceColumnId) return;

    const targetCol = boardData.columns.find(c => c.id === targetColumnId);
    if (!targetCol) return;

    // Calculate insert position
    let insertIdx = dragOverCardIdx ?? targetCol.cards.length;

    // If same column, adjust index for removal
    if (sourceColumnId === targetColumnId) {
      const currentIdx = targetCol.cards.findIndex(c => c.id === cardId);
      if (currentIdx === insertIdx || currentIdx === insertIdx - 1) {
        // No actual move needed
        setDragOverColumnId(null);
        setDragOverCardIdx(null);
        return;
      }
    }

    // Optimistic update
    setBoardData(prev => {
      if (!prev) return null;
      const newColumns = prev.columns.map(col => {
        if (col.id === sourceColumnId && col.id === targetColumnId) {
          // Same column reorder
          const cards = col.cards.filter(c => c.id !== cardId);
          const adjustedIdx = Math.min(insertIdx, cards.length);
          cards.splice(adjustedIdx > insertIdx ? adjustedIdx - 1 : adjustedIdx, 0, { ...sourceCard!, position: adjustedIdx });
          return { ...col, cards: cards.map((c, i) => ({ ...c, position: i })) };
        }
        if (col.id === sourceColumnId) {
          return { ...col, cards: col.cards.filter(c => c.id !== cardId).map((c, i) => ({ ...c, position: i })) };
        }
        if (col.id === targetColumnId) {
          const cards = [...col.cards];
          const adjustedIdx = Math.min(insertIdx, cards.length);
          cards.splice(adjustedIdx, 0, { ...sourceCard!, column_id: targetColumnId, position: adjustedIdx });
          return { ...col, cards: cards.map((c, i) => ({ ...c, position: i })) };
        }
        return col;
      });
      return { ...prev, columns: newColumns };
    });

    setDragOverColumnId(null);
    setDragOverCardIdx(null);

    try {
      if (sourceColumnId === targetColumnId) {
        // Same column reorder
        const col = boardData.columns.find(c => c.id === targetColumnId);
        if (col) {
          const cards = col.cards.filter(c => c.id !== cardId);
          const adjustedIdx = Math.min(insertIdx, cards.length);
          cards.splice(adjustedIdx, 0, sourceCard);
          await reorderCards(cards.map(c => c.id));
        }
      } else {
        // Move to different column
        await moveCard(cardId, targetColumnId, insertIdx);
        // Reorder cards in target column
        const newTargetCards = boardData.columns.find(c => c.id === targetColumnId)?.cards.filter(c => c.id !== cardId) || [];
        newTargetCards.splice(insertIdx, 0, sourceCard);
        await reorderCards(newTargetCards.map(c => c.id));
      }
    } catch (err: any) {
      setError(err.message);
      if (boardData.board.id) openBoard(boardData.board.id);
    }
  };

  /* ═══════════════════════════════════════════
     COLUMN DRAG & DROP (reorder columns)
     ═══════════════════════════════════════════ */
  const handleColDragStart = (e: React.DragEvent, colId: string) => {
    setDragColId(colId);
    setDragCardId(null); // Ensure card drag is off
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('application/kanban-column', colId);
    requestAnimationFrame(() => {
      const el = document.getElementById(`kanban-col-${colId}`);
      if (el) el.classList.add('kanban-column-dragging');
    });
  };

  const handleColDragEnd = () => {
    if (dragColId) {
      const el = document.getElementById(`kanban-col-${dragColId}`);
      if (el) el.classList.remove('kanban-column-dragging');
    }
    setDragColId(null);
    setDragOverColIdx(null);
  };

  const handleColDragOver = (e: React.DragEvent, colIndex: number) => {
    if (!dragColId) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    // Calculate insert position based on cursor
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const midX = rect.left + rect.width / 2;
    setDragOverColIdx(e.clientX < midX ? colIndex : colIndex + 1);
  };

  const handleColDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    const colId = e.dataTransfer.getData('application/kanban-column');
    if (!colId || !boardData || dragOverColIdx === null) {
      setDragOverColIdx(null);
      return;
    }

    const currentIdx = boardData.columns.findIndex(c => c.id === colId);
    if (currentIdx === -1) return;

    let targetIdx = dragOverColIdx;
    if (currentIdx < targetIdx) targetIdx--;
    if (currentIdx === targetIdx) {
      setDragOverColIdx(null);
      return;
    }

    // Optimistic reorder
    const newCols = [...boardData.columns];
    const [moved] = newCols.splice(currentIdx, 1);
    newCols.splice(targetIdx, 0, moved);
    const reordered = newCols.map((c, i) => ({ ...c, position: i }));

    setBoardData(prev => prev ? { ...prev, columns: reordered } : null);
    setDragOverColIdx(null);

    try {
      await reorderColumns(reordered.map(c => c.id));
    } catch (err: any) {
      setError(err.message);
      if (boardData.board.id) openBoard(boardData.board.id);
    }
  };

  /* ─── Media Picker ─── */
  const openMediaPicker = async () => {
    setShowMediaPicker(true);
    if (galleryAssets.length === 0 && orgId) {
      setGalleryLoading(true);
      try {
        const assets = await fetchOrgMediaAssets(orgId, { status: 'approved' });
        setGalleryAssets(assets);
        const cache = { ...mediaCache };
        assets.forEach(a => { cache[a.id] = a; });
        setMediaCache(cache);
        void backfillMissingThumbnails(orgId, assets, (id, thumbnailUrl) => {
          setGalleryAssets((prev) => prev.map((a) => (a.id === id ? { ...a, thumbnail_url: thumbnailUrl } : a)));
          setMediaCache((prev) => prev[id] ? { ...prev, [id]: { ...prev[id], thumbnail_url: thumbnailUrl } } : prev);
        });
      } catch (err: any) {
        setError(err.message);
      } finally {
        setGalleryLoading(false);
      }
    }
  };

  const toggleMediaSelection = (assetId: string) => {
    setCardMediaIds(prev =>
      prev.includes(assetId) ? prev.filter(id => id !== assetId) : [...prev, assetId]
    );
  };

  /* ─── Back to board list ─── */
  const goBack = () => {
    setView('list');
    setBoardData(null);
    loadBoards();
  };

  /* ─── Filter cards ─── */
  const filterCards = (cards: KanbanCard[]): KanbanCard[] => {
    if (!filterText.trim()) return cards;
    const q = filterText.toLowerCase();
    return cards.filter(c =>
      c.title.toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q) ||
      (c.assignee_ids || []).some(id => personName(id).toLowerCase().includes(q))
    );
  };

  /* ─── Render helpers ─── */

  const renderMediaThumbs = (ids: string[]) => {
    if (!ids.length) return null;
    const maxShow = 3;
    const shown = ids.slice(0, maxShow);
    const remaining = ids.length - maxShow;
    return (
      <div className="kanban-card-media">
        {shown.map(id => {
          const asset = mediaCache[id];
          if (!asset) return <div key={id} className="kanban-thumb-placeholder" />;
          const isImg = isImageAsset(asset);
          return (
            <div key={id} className="kanban-thumb">
              {isImg ? (
                <img src={galleryThumbSrc(asset)} alt="" loading="lazy" decoding="async" />
              ) : (
                <video src={asset.video_url} muted preload="metadata" />
              )}
              <div className="kanban-thumb-badge">
                {isImg ? <ImageIcon size={9} /> : <Film size={9} />}
              </div>
            </div>
          );
        })}
        {remaining > 0 && (
          <div className="kanban-thumb-more">+{remaining}</div>
        )}
      </div>
    );
  };

  /* ─── Loading state ─── */
  if (loading && !boardData && boards.length === 0) {
    return (
      <div className="glass-card wide-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '40vh', gap: 10, color: 'var(--text-muted)' }}>
        <Loader2 size={20} className="spin" /> Carregando…
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     BOARD LIST VIEW
     ═══════════════════════════════════════════ */
  if (view === 'list') {
    return (
      <div className="glass-card wide-card kanban-page">
        <div className="kanban-header">
          <div>
            <h1>Kanban</h1>
            <p className="subtitle">Quadros públicos são da empresa. Privados ficam com quem criou e com os administradores.</p>
          </div>
          <button className="kanban-btn-primary" onClick={() => setShowNewBoard(true)}>
            <Plus size={16} /> Novo Quadro
          </button>
        </div>

        {error && <div className="error-message" style={{ marginTop: '1rem' }}>{error}</div>}

        {/* New board form */}
        {showNewBoard && (
          <div className="kanban-new-board-form" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
            <input
              autoFocus
              value={newBoardTitle}
              onChange={e => setNewBoardTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateBoard()}
              placeholder="Nome do quadro…"
              maxLength={KANBAN_LIMITS.title}
              className="kanban-input"
            />
            <input
              value={newBoardDesc}
              onChange={e => setNewBoardDesc(e.target.value)}
              placeholder="Descrição (opcional)…"
              maxLength={KANBAN_LIMITS.description}
              className="kanban-input kanban-input-sm"
              style={{ marginTop: 6 }}
            />
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button
                type="button"
                className={`kanban-visibility-choice ${newBoardVisibility === 'org' ? 'active' : ''}`}
                onClick={() => setNewBoardVisibility('org')}
              >
                <Users size={14} /> Público na empresa
              </button>
              <button
                type="button"
                className={`kanban-visibility-choice ${newBoardVisibility === 'private' ? 'active' : ''}`}
                onClick={() => setNewBoardVisibility('private')}
              >
                <Lock size={14} /> Privado
              </button>
            </div>
            <p className="kanban-visibility-hint">
              {newBoardVisibility === 'private'
                ? 'Só você e os administradores da empresa veem este quadro.'
                : 'Todos os membros da empresa veem este quadro.'}
            </p>
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <button className="kanban-btn-primary kanban-btn-sm" onClick={handleCreateBoard} disabled={!newBoardTitle.trim()}>
                <Check size={14} /> Criar
              </button>
              <button className="kanban-btn-ghost kanban-btn-sm" onClick={() => { setShowNewBoard(false); setNewBoardTitle(''); setNewBoardDesc(''); setNewBoardVisibility('org'); }}>
                <X size={14} /> Cancelar
              </button>
            </div>
          </div>
        )}

        <div className="kanban-list-filters">
          {([
            ['all', 'Todos'],
            ['org', 'Públicos'],
            ['private', 'Privados'],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              type="button"
              className={`kanban-visibility-choice ${boardListFilter === id ? 'active' : ''}`}
              onClick={() => setBoardListFilter(id)}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Board cards grid */}
        <div className="kanban-boards-grid">
          {boards.filter(b => boardListFilter === 'all' || (b.visibility || 'org') === boardListFilter).length === 0 && !showNewBoard ? (
            <div className="kanban-empty">
              <LayoutGrid size={48} />
              <p>{boards.length === 0 ? 'Nenhum quadro criado' : 'Nenhum quadro neste filtro'}</p>
              <p className="subtitle">{boards.length === 0 ? 'Crie seu primeiro quadro Kanban para organizar projetos e anexar mídias.' : 'Troque o filtro ou crie um quadro com essa visibilidade.'}</p>
            </div>
          ) : (
            boards.filter(b => boardListFilter === 'all' || (b.visibility || 'org') === boardListFilter).map(board => (
              <div key={board.id} className="kanban-board-card" onClick={() => openBoard(board.id)}>
                <div className="kanban-board-card-header">
                  <LayoutGrid size={18} />
                  <span className="kanban-board-card-title">{board.title}</span>
                  <span className={`kanban-visibility-badge ${board.visibility === 'private' ? 'private' : 'org'}`}>
                    {board.visibility === 'private' ? <Lock size={11} /> : <Users size={11} />}
                    {board.visibility === 'private' ? 'Privado' : 'Público'}
                  </span>
                  <button
                    className="kanban-btn-icon kanban-btn-danger-hover"
                    onClick={(e) => handleDeleteBoard(board.id, e)}
                    title="Excluir quadro"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                {board.description && (
                  <p className="kanban-board-card-desc">{board.description}</p>
                )}
                <div className="kanban-board-card-footer">
                  <span>{board.column_count} {board.column_count === 1 ? 'coluna' : 'colunas'} · {board.card_count} {board.card_count === 1 ? 'card' : 'cards'}</span>
                  <span style={{ marginLeft: 'auto' }}>{board.created_at ? new Date(board.created_at).toLocaleDateString('pt-BR') : ''}</span>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  /* ═══════════════════════════════════════════
     BOARD DETAIL VIEW
     ═══════════════════════════════════════════ */
  if (!boardData) return null;

  return (
    <div className="kanban-page kanban-board-view">
      {/* Board header */}
      <div className="kanban-board-header">
        <button className="kanban-btn-ghost" onClick={goBack}>
          <ArrowLeft size={18} /> Voltar
        </button>

        {/* Inline-editable board title */}
        {editingBoardTitle ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
            <input
              autoFocus
              value={boardTitleDraft}
              onChange={e => setBoardTitleDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveBoardTitle(); if (e.key === 'Escape') setEditingBoardTitle(false); }}
              className="kanban-input"
              style={{ fontSize: '1.1rem', fontWeight: 700, maxWidth: 300 }}
            />
            <button className="kanban-btn-icon" onClick={handleSaveBoardTitle}><Check size={16} /></button>
            <button className="kanban-btn-icon" onClick={() => setEditingBoardTitle(false)}><X size={16} /></button>
          </div>
        ) : (
          <h2
            className="kanban-board-title"
            onClick={() => { setEditingBoardTitle(true); setBoardTitleDraft(boardData.board.title); }}
            title="Clique para renomear"
            style={{ cursor: 'pointer' }}
          >
            {boardData.board.title}
            <Edit3 size={13} style={{ marginLeft: 6, opacity: 0.4, verticalAlign: 'middle' }} />
          </h2>
        )}

        {(role === 'admin' || boardData.board.created_by === user?.id) ? (
          <KanbanSelect
            className="kanban-visibility-select"
            value={boardData.board.visibility || 'org'}
            onChange={v => handleSetVisibility(v as BoardVisibility)}
            title="Quem pode ver este quadro"
            options={[
              { value: 'org', label: 'Público na empresa' },
              { value: 'private', label: 'Privado' },
            ]}
          />
        ) : (
          <span className={`kanban-visibility-badge ${boardData.board.visibility === 'private' ? 'private' : 'org'}`}>
            {boardData.board.visibility === 'private' ? 'Privado' : 'Público na empresa'}
          </span>
        )}

        {/* Toolbar */}
        <div className="kanban-toolbar">
          <div className="kanban-search-box">
            <Search size={14} />
            <input
              value={filterText}
              onChange={e => setFilterText(e.target.value)}
              placeholder="Filtrar cards ou responsáveis…"
              className="kanban-search-input"
            />
            {filterText && (
              <button className="kanban-btn-icon" onClick={() => setFilterText('')} style={{ width: 20, height: 20 }}>
                <X size={12} />
              </button>
            )}
          </div>
          <button className="kanban-btn-outline" onClick={() => setShowFieldsModal(true)} title="Gerenciar Campos Personalizados">
            <Settings2 size={14} /> Campos
          </button>
          <button className="kanban-btn-outline" onClick={() => setShowNewColumn(true)}>
            <Plus size={14} /> Coluna
          </button>
        </div>
      </div>

      {error && <div className="error-message" style={{ margin: '0 1.5rem 0.5rem' }}>{error}</div>}

      {/* Columns container */}
      <div className="kanban-columns-container">
        {boardData.columns.map((col, colIdx) => {
          const isCollapsed = collapsedCols.has(col.id);
          const filteredCards = filterCards(col.cards);
          const wipLimit = wipLimits[col.id];
          const isOverWip = wipLimit && wipLimit > 0 && col.cards.length > wipLimit;

          // Collapsed column render
          if (isCollapsed) {
            return (
              <div
                key={col.id}
                className="kanban-column kanban-column-collapsed"
                onClick={() => toggleCollapse(col.id)}
                title="Clique para expandir"
              >
                <div className="kanban-collapsed-bar" style={{ background: col.color || '#6366f1' }} />
                <span className="kanban-collapsed-title">{col.title}</span>
                <span className="kanban-column-count">{col.cards.length}</span>
                <ChevronRight size={14} style={{ opacity: 0.5 }} />
              </div>
            );
          }

          return (
            <div
              key={col.id}
              id={`kanban-col-${col.id}`}
              className={`kanban-column ${dragOverColumnId === col.id && dragCardId ? 'kanban-column-drag-over' : ''}`}
              draggable
              onDragStart={(e) => {
                // Only allow column drag from header area
                const target = e.target as HTMLElement;
                if (target.closest('.kanban-cards-list') || target.closest('.kanban-add-card-btn') || target.closest('.kanban-quick-add')) {
                  e.preventDefault();
                  return;
                }
                handleColDragStart(e, col.id);
              }}
              onDragEnd={() => { handleCardDragEnd(); handleColDragEnd(); }}
              onDragOver={(e) => {
                if (dragCardId) handleCardDragOverColumn(e, col.id);
                if (dragColId) handleColDragOver(e, colIdx);
              }}
              onDragLeave={(e) => {
                if (dragCardId) handleCardDragLeaveColumn(e, col.id);
              }}
              onDrop={(e) => {
                if (dragCardId) handleCardDrop(e, col.id);
                if (dragColId) handleColDrop(e);
              }}
            >
              {/* Column drag indicator */}
              {dragColId && dragOverColIdx === colIdx && (
                <div className="kanban-col-drop-indicator kanban-col-drop-left" />
              )}

              {/* Column header */}
              <div className="kanban-column-header" style={{ borderTopColor: col.color || '#6366f1' }}>
                {editingColumnId === col.id ? (
                  <div className="kanban-column-edit">
                    <input
                      autoFocus
                      value={editingColumnTitle}
                      onChange={e => setEditingColumnTitle(e.target.value)}
                      onKeyDown={e => { if (e.key === 'Enter') handleUpdateColumnTitle(col.id); if (e.key === 'Escape') setEditingColumnId(null); }}
                      className="kanban-input kanban-input-sm"
                    />
                    <button className="kanban-btn-icon" onClick={() => handleUpdateColumnTitle(col.id)}><Check size={14} /></button>
                    <button className="kanban-btn-icon" onClick={() => setEditingColumnId(null)}><X size={14} /></button>
                  </div>
                ) : (
                  <>
                    <div className="kanban-column-title-row">
                      <div className="kanban-column-dot" style={{ background: col.color || '#6366f1' }} />
                      <span className="kanban-column-title">{col.title}</span>
                      <span className={`kanban-column-count ${isOverWip ? 'kanban-wip-over' : ''}`}>
                        {col.cards.length}{wipLimit && wipLimit > 0 ? `/${wipLimit}` : ''}
                      </span>
                      {isOverWip && <AlertTriangle size={13} className="kanban-wip-icon" />}
                    </div>
                    <div style={{ display: 'flex', gap: 2 }}>
                      <button className="kanban-btn-icon" onClick={() => toggleCollapse(col.id)} title="Colapsar">
                        <Minus size={13} />
                      </button>
                      <div style={{ position: 'relative' }}>
                        <button className="kanban-btn-icon" onClick={() => setColumnMenuId(columnMenuId === col.id ? null : col.id)}>
                          <MoreHorizontal size={14} />
                        </button>
                        {columnMenuId === col.id && (
                          <div ref={colMenuRef} className="kanban-dropdown">
                            <button onClick={() => { setEditingColumnId(col.id); setEditingColumnTitle(col.title); setColumnMenuId(null); }}>
                              <Edit3 size={13} /> Renomear
                            </button>
                            <button onClick={() => { setShowColorPicker(col.id); }}>
                              <Palette size={13} /> Cor da Coluna
                            </button>
                            {showColorPicker === col.id && (
                              <div className="kanban-color-picker">
                                {COLUMN_COLORS.map(c => (
                                  <button
                                    key={c}
                                    className={`kanban-color-swatch ${col.color === c ? 'active' : ''}`}
                                    style={{ background: c }}
                                    onClick={() => handleUpdateColumnColor(col.id, c)}
                                  />
                                ))}
                              </div>
                            )}
                            <button onClick={() => { setShowWipInput(col.id); setWipDraft(String(wipLimits[col.id] || '')); }}>
                              <ChevronsLeftRight size={13} /> Limite WIP
                            </button>
                            {showWipInput === col.id && (
                              <div className="kanban-wip-input-row">
                                <input
                                  autoFocus
                                  type="number"
                                  min="0"
                                  value={wipDraft}
                                  onChange={e => setWipDraft(e.target.value)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSetWipLimit(col.id); if (e.key === 'Escape') { setShowWipInput(null); setColumnMenuId(null); } }}
                                  placeholder="0 = sem limite"
                                  className="kanban-input kanban-input-sm"
                                  style={{ width: 120 }}
                                />
                                <button className="kanban-btn-icon" onClick={() => handleSetWipLimit(col.id)}><Check size={12} /></button>
                              </div>
                            )}
                            <button className="danger" onClick={() => { handleDeleteColumn(col.id); setColumnMenuId(null); }}>
                              <Trash2 size={13} /> Excluir
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* WIP Warning banner */}
              {isOverWip && (
                <div className="kanban-wip-warning">
                  <AlertTriangle size={12} /> Limite WIP excedido ({col.cards.length}/{wipLimit})
                </div>
              )}

              {/* Cards */}
              <div className="kanban-cards-list">
                {filteredCards.map((card, cardIdx) => (
                  <React.Fragment key={card.id}>
                    {/* Drop indicator line */}
                    {dragCardId && dragOverColumnId === col.id && dragOverCardIdx === cardIdx && (
                      <div className="kanban-drop-indicator" />
                    )}
                    <div
                      id={`kanban-card-${card.id}`}
                      className="kanban-card"
                      role="button"
                      tabIndex={0}
                      aria-label={card.title}
                      draggable
                      onDragStart={(e) => { e.stopPropagation(); handleCardDragStart(e, card.id); }}
                      onDragEnd={handleCardDragEnd}
                      onDragOver={(e) => handleCardDragOverCard(e, col.id, cardIdx)}
                      onClick={() => openCardModal('edit', col.id, card)}
                    >
                      <div className="kanban-card-grip">
                        <GripVertical size={12} />
                      </div>
                      <div className="kanban-card-content">
                        <div className="kanban-card-title">{card.title}</div>
                        {card.description && (
                          <div className="kanban-card-desc">{card.description}</div>
                        )}
                        {(card.assignee_ids || []).length > 0 && (
                          <div className="kanban-card-assignees">
                            {(card.assignee_ids || []).map(id => (
                              <span key={id} className="kanban-assignee-chip" title="Responsável">
                                {personName(id)}
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Badges de Campos Personalizados */}
                        {boardData.board.custom_fields && boardData.board.custom_fields.length > 0 && card.custom_field_values && (
                          <div className="kanban-card-fields">
                            {boardData.board.custom_fields.map(f => {
                              const val = card.custom_field_values?.[f.id];
                              if (val === undefined || val === null || val === '') return null;
                              if (f.type === 'checkbox' && !val) return null;
                              return (
                                <span key={f.id} className="kanban-field-badge" title={f.name}>
                                  {getFieldIcon(f.type)}
                                  <span className="kanban-field-badge-val">
                                    {f.type === 'checkbox' ? f.name : f.type === 'date' ? new Date(val + 'T00:00:00').toLocaleDateString('pt-BR') : String(val)}
                                  </span>
                                </span>
                              );
                            })}
                          </div>
                        )}
                        {renderMediaThumbs(card.media_asset_ids || [])}
                        {(card.media_asset_ids?.length || 0) > 0 && (
                          <div className="kanban-card-attach-count">
                            <Paperclip size={11} /> {card.media_asset_ids?.length || 0}
                          </div>
                        )}
                      </div>
                    </div>
                  </React.Fragment>
                ))}
                {/* Drop indicator at end */}
                {dragCardId && dragOverColumnId === col.id && dragOverCardIdx === filteredCards.length && (
                  <div className="kanban-drop-indicator" />
                )}
              </div>

              {/* Quick-add card inline */}
              {quickAddColId === col.id ? (
                <div className="kanban-quick-add">
                  <input
                    ref={quickAddRef}
                    autoFocus
                    value={quickAddTitle}
                    onChange={e => setQuickAddTitle(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') handleQuickAdd(col.id);
                      if (e.key === 'Escape') { setQuickAddColId(null); setQuickAddTitle(''); }
                    }}
                    placeholder="Título do card…"
                    className="kanban-input kanban-input-sm"
                  />
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <button className="kanban-btn-primary kanban-btn-sm" onClick={() => handleQuickAdd(col.id)} disabled={!quickAddTitle.trim()}>
                      <Plus size={12} /> Adicionar
                    </button>
                    <button className="kanban-btn-ghost kanban-btn-sm" onClick={() => { setQuickAddColId(null); setQuickAddTitle(''); }}>
                      <X size={12} />
                    </button>
                  </div>
                </div>
              ) : (
                <button className="kanban-add-card-btn" onClick={() => openCardModal('create', col.id)}>
                  <Plus size={14} /> Novo Card
                </button>
              )}
            </div>
          );
        })}

        {/* Column drop indicator at end */}
        {dragColId && dragOverColIdx === boardData.columns.length && (
          <div className="kanban-col-drop-indicator-end" />
        )}

        {/* New column form (inline) */}
        {showNewColumn && (
          <div className="kanban-column kanban-column-new">
            <input
              autoFocus
              value={newColumnTitle}
              onChange={e => setNewColumnTitle(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') handleCreateColumn(); if (e.key === 'Escape') { setShowNewColumn(false); setNewColumnTitle(''); } }}
              placeholder="Nome da coluna…"
              className="kanban-input"
            />
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="kanban-btn-primary kanban-btn-sm" onClick={handleCreateColumn} disabled={!newColumnTitle.trim()}>
                <Check size={14} /> Criar
              </button>
              <button className="kanban-btn-ghost kanban-btn-sm" onClick={() => { setShowNewColumn(false); setNewColumnTitle(''); }}>
                Cancelar
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ─── Card Modal ─── */}
      {cardModal && (
        <div className="kanban-modal-overlay" onClick={() => setCardModal(null)}>
          <div className="kanban-modal" onClick={e => e.stopPropagation()}>
            <div className="kanban-modal-header">
              <h3>{cardModal.mode === 'create' ? 'Novo Card' : 'Editar Card'}</h3>
              <button className="kanban-btn-icon" onClick={() => setCardModal(null)}><X size={18} /></button>
            </div>

            <div className="kanban-modal-body">
              <label className="kanban-label">Título</label>
              <input
                autoFocus
                value={cardTitle}
                onChange={e => setCardTitle(e.target.value)}
                placeholder="Ex: Fotos do apartamento Leblon"
                maxLength={KANBAN_LIMITS.title}
                className="kanban-input"
              />

              <label className="kanban-label">Descrição</label>
              <textarea
                value={cardDesc}
                onChange={e => setCardDesc(e.target.value)}
                placeholder="Detalhes opcionais…"
                maxLength={KANBAN_LIMITS.description}
                className="kanban-textarea"
                rows={3}
              />

              <label className="kanban-label">Responsáveis</label>
              <div className="kanban-assignee-picker">
                {cardAssignees.map(id => (
                  <button
                    type="button"
                    key={id}
                    className="kanban-assignee-chip removable"
                    onClick={() => setCardAssignees(prev => prev.filter(x => x !== id))}
                    title="Remover"
                  >
                    {personName(id)} <X size={10} />
                  </button>
                ))}
                <KanbanSelect
                  value=""
                  placeholder="Mencionar alguém da empresa…"
                  onChange={id => {
                    if (!id) return;
                    setCardAssignees(prev => prev.includes(id) ? prev : [...prev, id]);
                  }}
                  options={[
                    { value: '', label: 'Mencionar alguém da empresa…' },
                    ...people.filter(p => !cardAssignees.includes(p.user_id)).map(p => ({
                      value: p.user_id,
                      label: p.full_name?.trim() || 'Membro',
                    })),
                  ]}
                />
              </div>

              {/* Seção de Campos Personalizados no Modal do Card */}
              <div className="kanban-modal-section-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '12px', marginBottom: '8px' }}>
                <label className="kanban-label" style={{ margin: 0 }}>Campos Personalizados</label>
                <button
                  type="button"
                  className="kanban-btn-ghost kanban-btn-sm"
                  onClick={() => setShowFieldsModal(true)}
                  style={{ fontSize: '0.75rem', padding: '2px 8px', display: 'flex', alignItems: 'center', gap: 4 }}
                >
                  <Settings2 size={12} /> Gerenciar
                </button>
              </div>

              {(!boardData?.board.custom_fields || boardData.board.custom_fields.length === 0) ? (
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '16px', background: 'rgba(255,255,255,0.02)', padding: '8px 12px', borderRadius: '8px', border: '1px dashed rgba(255,255,255,0.08)' }}>
                  Nenhum campo configurado neste quadro. Clique em <strong>Gerenciar</strong> para criar campos como Prioridade, Valor, Data Limite, etc.
                </div>
              ) : (
                <div className="kanban-card-custom-inputs" style={{ marginBottom: '16px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', background: 'rgba(0,0,0,0.15)', padding: '12px', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  {boardData.board.custom_fields.map(field => {
                    const value = cardCustomValues[field.id];
                    return (
                      <div key={field.id} className="kanban-custom-field-row">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 5, marginBottom: 4, fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 600 }}>
                          {getFieldIcon(field.type)} {field.name}
                        </label>
                        {field.type === 'text' && (
                          <input
                            value={value || ''}
                            onChange={e => setCardCustomValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                            placeholder="Texto..."
                            className="kanban-input kanban-input-sm"
                            style={{ width: '100%' }}
                          />
                        )}
                        {field.type === 'number' && (
                          <input
                            type="number"
                            value={value !== undefined ? value : ''}
                            onChange={e => setCardCustomValues(prev => ({ ...prev, [field.id]: e.target.value !== '' ? Number(e.target.value) : '' }))}
                            placeholder="0"
                            className="kanban-input kanban-input-sm"
                            style={{ width: '100%' }}
                          />
                        )}
                        {field.type === 'date' && (
                          <input
                            type="date"
                            value={value || ''}
                            onChange={e => setCardCustomValues(prev => ({ ...prev, [field.id]: e.target.value }))}
                            className="kanban-input kanban-input-sm"
                            style={{ width: '100%' }}
                          />
                        )}
                        {field.type === 'checkbox' && (
                          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', background: 'rgba(255,255,255,0.04)', padding: '6px 10px', borderRadius: '6px', border: '1px solid rgba(255,255,255,0.08)', height: '34px' }}>
                            <input
                              type="checkbox"
                              checked={!!value}
                              onChange={e => setCardCustomValues(prev => ({ ...prev, [field.id]: e.target.checked }))}
                              style={{ width: 16, height: 16, cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '0.8rem', color: 'var(--text-main)' }}>{field.name}</span>
                          </label>
                        )}
                        {field.type === 'select' && (
                          <KanbanSelect
                            value={value || ''}
                            placeholder="Selecione..."
                            onChange={v => setCardCustomValues(prev => ({ ...prev, [field.id]: v }))}
                            options={[
                              { value: '', label: 'Selecione...' },
                              ...(field.options || []).map(opt => ({ value: opt, label: opt })),
                            ]}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="kanban-inline-field">
                <label className="kanban-label">Novo campo neste quadro</label>
                <div className="kanban-inline-field-row">
                  <input
                    value={cardFieldName}
                    onChange={e => setCardFieldName(e.target.value)}
                    placeholder="Nome do campo"
                    maxLength={KANBAN_LIMITS.fieldName}
                    className="kanban-input kanban-input-sm"
                  />
                  <KanbanSelect
                    value={cardFieldType}
                    onChange={v => setCardFieldType(v as CustomFieldType)}
                    options={[
                      { value: 'text', label: 'Texto' },
                      { value: 'number', label: 'Número' },
                      { value: 'date', label: 'Data' },
                      { value: 'checkbox', label: 'Caixa' },
                      { value: 'select', label: 'Lista' },
                    ]}
                  />
                  <button
                    type="button"
                    className="kanban-btn-outline kanban-btn-sm"
                    onClick={handleAddFieldFromCard}
                    disabled={!cardFieldName.trim()}
                  >
                    <Plus size={14} /> Criar
                  </button>
                </div>
                {cardFieldType === 'select' && (
                  <input
                    value={cardFieldOptions}
                    onChange={e => setCardFieldOptions(e.target.value)}
                    placeholder="Opções separadas por vírgula"
                    className="kanban-input kanban-input-sm"
                    style={{ marginTop: 6 }}
                  />
                )}
              </div>

              <label className="kanban-label">
                <Paperclip size={13} /> Mídias Anexadas ({cardMediaIds.length})
              </label>

              {/* Selected media thumbnails */}
              {cardMediaIds.length > 0 && (
                <div className="kanban-selected-media">
                  {cardMediaIds.map(id => {
                    const asset = mediaCache[id] || galleryAssets.find(a => a.id === id);
                    if (!asset) return (
                      <div key={id} className="kanban-selected-thumb">
                        <div className="kanban-thumb-placeholder" />
                        <button className="kanban-thumb-remove" onClick={() => toggleMediaSelection(id)}>
                          <X size={10} />
                        </button>
                      </div>
                    );
                    const isImg = isImageAsset(asset);
                    return (
                      <div key={id} className="kanban-selected-thumb">
                        {isImg ? (
                          <img src={galleryThumbSrc(asset)} alt="" loading="lazy" decoding="async" />
                        ) : (
                          <video src={asset.video_url} muted preload="metadata" />
                        )}
                        <button className="kanban-thumb-remove" onClick={() => toggleMediaSelection(id)}>
                          <X size={10} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              <button className="kanban-btn-outline kanban-btn-sm" onClick={openMediaPicker} style={{ marginTop: 8 }}>
                <ImageIcon size={14} /> Selecionar da Galeria
              </button>

              {/* Media Picker inline */}
              {showMediaPicker && (
                <div className="kanban-media-picker">
                  <div className="kanban-media-picker-header">
                    <span>Galeria de Mídia</span>
                    <button className="kanban-btn-icon" onClick={() => setShowMediaPicker(false)}><X size={14} /></button>
                  </div>
                  {galleryLoading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', gap: 8, color: 'var(--text-muted)' }}>
                      <Loader2 size={16} className="spin" /> Carregando…
                    </div>
                  ) : galleryAssets.length === 0 ? (
                    <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
                      Nenhuma mídia aprovada na galeria.
                    </div>
                  ) : (
                    <div className="kanban-media-grid">
                      {galleryAssets.map(asset => {
                        const selected = cardMediaIds.includes(asset.id);
                        const isImg = isImageAsset(asset);
                        return (
                          <div
                            key={asset.id}
                            className={`kanban-media-item ${selected ? 'selected' : ''}`}
                            onClick={() => toggleMediaSelection(asset.id)}
                          >
                            {isImg ? (
                              <img src={galleryThumbSrc(asset)} alt="" loading="lazy" decoding="async" />
                            ) : (
                              <video src={asset.video_url} muted preload="metadata" />
                            )}
                            <div className="kanban-media-item-overlay">
                              <div className={`kanban-media-checkbox ${selected ? 'checked' : ''}`}>
                                {selected && <Check size={10} />}
                              </div>
                              <span className="kanban-media-item-name">{assetDisplayName(asset)}</span>
                            </div>
                            <div className="kanban-media-type-badge">
                              {isImg ? <ImageIcon size={10} /> : <Film size={10} />}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="kanban-modal-footer">
              {cardModal.mode === 'edit' && cardModal.card && (
                <button
                  className="kanban-btn-danger kanban-btn-sm"
                  onClick={() => handleDeleteCard(cardModal.card!.id, cardModal.columnId)}
                >
                  <Trash2 size={14} /> Excluir
                </button>
              )}
              <div style={{ flex: 1 }} />
              <button className="kanban-btn-ghost kanban-btn-sm" onClick={() => setCardModal(null)}>
                Cancelar
              </button>
              <button className="kanban-btn-primary kanban-btn-sm" onClick={handleSaveCard} disabled={!cardTitle.trim()}>
                <Check size={14} /> {cardModal.mode === 'create' ? 'Criar' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ─── Modal de Gerenciamento dos Campos Personalizados ─── */}
      {showFieldsModal && (
        <div className="kanban-modal-overlay" onClick={() => setShowFieldsModal(false)} style={{ zIndex: 9999 }}>
          <div className="kanban-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <div className="kanban-modal-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Settings2 size={18} className="text-primary" />
                <h3>Campos Personalizados</h3>
              </div>
              <button className="kanban-btn-icon" onClick={() => setShowFieldsModal(false)}><X size={18} /></button>
            </div>
            <p className="subtitle" style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: '1.2rem' }}>
              Defina os campos que ficarão disponíveis para preenchimento em todos os cartões deste quadro.
            </p>

            <div className="kanban-fields-list" style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '280px', overflowY: 'auto', marginBottom: '1.5rem' }}>
              {(!boardData?.board.custom_fields || boardData.board.custom_fields.length === 0) ? (
                <div style={{ padding: '1.5rem', textAlign: 'center', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                  Nenhum campo personalizado cadastrado neste quadro.
                </div>
              ) : (
                boardData.board.custom_fields.map(field => (
                  <div key={field.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', padding: '10px 12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: 6, background: 'rgba(99,102,241,0.15)', color: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {getFieldIcon(field.type)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--text-main)' }}>{field.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>
                          Tipo: <span style={{ color: '#fff' }}>{field.type}</span>
                          {field.type === 'select' && field.options && ` (${field.options.join(', ')})`}
                        </div>
                      </div>
                    </div>
                    <button
                      className="kanban-btn-icon kanban-btn-danger-hover"
                      onClick={() => handleRemoveCustomField(field.id)}
                      title="Remover campo"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                ))
              )}
            </div>

            <div style={{ background: 'rgba(0,0,0,0.25)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px' }}>
              <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-main)', marginBottom: '10px' }}>Adicionar Novo Campo</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: '8px', marginBottom: '8px' }}>
                <div>
                  <label style={{ fontSize: '0.72rem', display: 'block', color: 'var(--text-muted)', marginBottom: 4 }}>Nome do Campo</label>
                  <input
                    value={newFieldName}
                    onChange={e => setNewFieldName(e.target.value)}
                    placeholder="Ex: Prioridade, Data Entrega..."
                    maxLength={KANBAN_LIMITS.fieldName}
                    className="kanban-input kanban-input-sm"
                    style={{ width: '100%' }}
                    onKeyDown={e => e.key === 'Enter' && handleAddCustomField()}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.72rem', display: 'block', color: 'var(--text-muted)', marginBottom: 4 }}>Tipo do Campo</label>
                  <KanbanSelect
                    value={newFieldType}
                    onChange={v => setNewFieldType(v as CustomFieldType)}
                    options={[
                      { value: 'text', label: 'Texto (Linha simples)' },
                      { value: 'number', label: 'Número (R$, Metragem)' },
                      { value: 'date', label: 'Data (Calendário)' },
                      { value: 'checkbox', label: 'Checkbox (Sim/Não)' },
                      { value: 'select', label: 'Seleção (Opções)' },
                    ]}
                  />
                </div>
              </div>
              {newFieldType === 'select' && (
                <div style={{ marginBottom: '8px' }}>
                  <label style={{ fontSize: '0.72rem', display: 'block', color: 'var(--text-muted)', marginBottom: 4 }}>Opções de Seleção (separadas por vírgula)</label>
                  <input
                    value={newFieldOptions}
                    onChange={e => setNewFieldOptions(e.target.value)}
                    placeholder="Ex: Alta, Média, Baixa"
                    className="kanban-input kanban-input-sm"
                    style={{ width: '100%' }}
                  />
                </div>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
                <button
                  type="button"
                  className="kanban-btn-primary kanban-btn-sm"
                  onClick={handleAddCustomField}
                  disabled={!newFieldName.trim()}
                >
                  <Plus size={14} /> Cadastrar Campo
                </button>
              </div>
            </div>

            <div className="kanban-modal-footer" style={{ marginTop: '1rem' }}>
              <div style={{ flex: 1 }} />
              <button className="kanban-btn-primary kanban-btn-sm" onClick={() => setShowFieldsModal(false)}>
                Concluir
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.spin { animation: spin 0.8s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
